import { useEffect, useState } from 'react';

import { win } from '@utils/win';

/**
 * Detects if the user's system is set to dark mode
 */
// ts-unused-exports:disable-next-line
export const getSystemThemePreference = (): 'dark' | 'light' => {
  if (
    win.matchMedia &&
    // eslint-disable-next-line lingui/no-unlocalized-strings
    win.matchMedia('(prefers-color-scheme: dark)').matches
  ) {
    return 'dark';
  }
  return 'light';
};

/**
 * Applies the appropriate theme class to the document element
 */
export const applyThemeClass = (theme: 'dark' | 'light' | 'system'): void => {
  const effectiveTheme =
    theme === 'system' ? getSystemThemePreference() : theme;

  if (effectiveTheme === 'dark') {
    addThemeDarkClass();
  } else {
    removeThemeDarkClass();
  }
};

/**
 * Resolves the effective theme value
 * If theme is 'system', returns the actual system preference ('light' or 'dark')
 * Otherwise returns the theme as-is (normalized to lowercase)
 */
export const getEffectiveTheme = (
  theme: 'dark' | 'light' | 'system' | string,
): 'dark' | 'light' | string => {
  const normalizedTheme = theme?.toLowerCase();
  if (normalizedTheme === 'system') {
    return getSystemThemePreference();
  }
  return normalizedTheme;
};

/**
 * Sets up a listener for system theme changes
 * Returns a cleanup function to remove the listener
 */
export const listenToSystemThemeChanges = (
  callback: (isDark: boolean) => void,
): (() => void) => {
  if (!win.matchMedia) {
    return () => {};
  }

  // eslint-disable-next-line lingui/no-unlocalized-strings
  const mediaQuery = win.matchMedia('(prefers-color-scheme: dark)');

  const handler = (e: MediaQueryListEvent): void => {
    callback(e.matches);
  };

  // Modern browsers
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }

  return () => {};
};

export const hasThemeDarkClass = (): boolean =>
  document.documentElement.classList.contains('theme-dark');

const addThemeDarkClass = (): void => {
  document.documentElement.classList.add('theme-dark');
};

const removeThemeDarkClass = (): void => {
  document.documentElement.classList.remove('theme-dark');
};

/**
 * Adds the button-dark-mode-enabled class to enable dark mode button styles
 * This should be called when ME-449354 flag is enabled
 */
export const addButtonDarkModeClass = (): void => {
  document.documentElement.classList.add('button-dark-mode-enabled');
};

/**
 * Removes the button-dark-mode-enabled class to disable dark mode button styles
 */
export const removeButtonDarkModeClass = (): void => {
  document.documentElement.classList.remove('button-dark-mode-enabled');
};

/**
 * Reactive hook that returns whether dark theme is currently active.
 * Watches for changes to the `theme-dark` class on `document.documentElement`.
 *
 * This replaces the static `USE_THEME_DARK` constant for components that need
 * to react to theme changes (e.g., SpectrumProvider colorScheme).
 */
export const useIsDarkTheme = (): boolean => {
  const [isDark, setIsDark] = useState(() => hasThemeDarkClass());

  useEffect(() => {
    const checkTheme = (): void => {
      const hasDarkClass = hasThemeDarkClass();
      setIsDark(hasDarkClass);
    };

    // Check on mount in case class changed between initial render and effect
    checkTheme();

    // Watch for theme class changes via MutationObserver
    const observer = new MutationObserver(() => {
      checkTheme();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  return isDark;
};
